import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import posthog from "posthog-js";

/**
 * Shared recording flow logic for NewRecordingModal.
 * Used by Recordings page and AddRecordingFlowWrapper (route config).
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Called when a recording is saved. Receives updatedRecordings array.
 */
export function useRecordingFlow({ onSuccess }) {
  const [record_type, setRecordType] = useState("upload_mp3");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [buttonState, setButtonState] = useState("record");
  const [time, setTime] = useState(0);
  const [maxTime, setMaxTime] = useState(30);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [step, setStep] = useState(1);
  const [recording_data, setRecordingData] = useState({ title: "", type: "" });
  const [isConverting, setIsConverting] = useState(false);
  const [loader, setLoader] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  const mediaRecorderRef = useRef(null);
  const audioUrlRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const intervalIdRef = useRef(null);

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const writeString = useCallback((view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }, []);

  const createWAVFile = useCallback(
    (audioBuffer) => {
      const numChannels = 1;
      const sampleRate = 8000;
      const bitDepth = 16;
      const length = audioBuffer.length;
      const buffer = new ArrayBuffer(44 + length * 2);
      const view = new DataView(buffer);
      writeString(view, 0, "RIFF");
      view.setUint32(4, 36 + length * 2, true);
      writeString(view, 8, "WAVE");
      writeString(view, 12, "fmt ");
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, numChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
      view.setUint16(32, numChannels * (bitDepth / 8), true);
      view.setUint16(34, bitDepth, true);
      writeString(view, 36, "data");
      view.setUint32(40, length * 2, true);
      const channelData = audioBuffer.getChannelData(0);
      let offset = 44;
      for (let i = 0; i < length; i++) {
        const sample = Math.max(-1, Math.min(1, channelData[i]));
        view.setInt16(offset, sample * 0x7fff, true);
        offset += 2;
      }
      return buffer;
    },
    [writeString]
  );

  const convertToWav = useCallback(
    async (audioBuffer) => {
      const offlineCtx = new OfflineAudioContext(1, audioBuffer.duration * 8000, 8000);
      const source = offlineCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(offlineCtx.destination);
      source.start();
      const renderedBuffer = await offlineCtx.startRendering();
      const wavBuffer = createWAVFile(renderedBuffer);
      return new Blob([wavBuffer], { type: "audio/wav" });
    },
    [createWAVFile]
  );

  const processAudioBlob = useCallback(
    async (blob) => {
      try {
        setIsConverting(true);
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
        const wavBlob = await convertToWav(audioBuffer);
        setAudioBlob(wavBlob);
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current);
        }
        const url = URL.createObjectURL(wavBlob);
        audioUrlRef.current = url;
        setAudioPreviewUrl(url);
        setIsRecording(false);
      } catch (error) {
        // Helps diagnose audio conversion failures (codec/format issues)
        console.error("Error processing audio:", error);
      } finally {
        setIsConverting(false);
      }
    },
    [convertToWav]
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await processAudioBlob(blob);
      };
      mediaRecorder.onstart = () => {
        setIsRecording(true);
        mediaRecorder.requestData();
        setTimeout(() => {
          if (mediaRecorder.state === "recording") stopRecording();
        }, 30000);
      };
      mediaRecorder.start(1000);
    } catch (error) {
      // User may have denied permission or device unavailable
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
    }
  }, [processAudioBlob, stopRecording]);

  const stopPlaying = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const playRecording = useCallback(() => {
    if (!audioUrlRef.current) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrlRef.current);
      setMaxTime(30);
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    intervalIdRef.current = setInterval(() => {
      setTime(audioRef.current.currentTime);
      if (audioRef.current.currentTime >= maxTime) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }, 1000);
    audioRef.current.onended = () => {
      setButtonState("play");
      stopPlaying();
    };
  }, [maxTime, stopPlaying]);

  const restartRecording = useCallback(() => {
    setUploadedFile(null);
    stopRecording();
    setTime(0);
    setAudioBlob(null);
    setAudioPreviewUrl(null);
    setButtonState("record");
    setMaxTime(30);
    mediaStreamRef.current = null;
    audioRef.current = null;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    mediaRecorderRef.current = null;
    intervalIdRef.current = null;
  }, [stopRecording]);

  const handleClick = useCallback(() => {
    if (buttonState === "record") {
      startRecording();
      setButtonState("stop");
    } else if (buttonState === "stop") {
      stopRecording();
      setButtonState("play");
    } else if (buttonState === "play") {
      playRecording();
      setButtonState("playing");
    } else {
      stopPlaying();
      setButtonState("play");
    }
  }, [buttonState, startRecording, stopRecording, playRecording, stopPlaying]);

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const maxSize = 25 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackMsg("Uploaded file too large. Please upload a file less than 25MB");
        setOpenSnack(true);
        event.target.value = "";
        setUploadedFile(null);
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["mp3", "m4a", "wav"].includes(ext)) {
        setSnackMsg("Unsupported file type. Please upload mp3, m4a, or wav.");
        setOpenSnack(true);
        event.target.value = "";
        return;
      }
      try {
        setIsConverting(true);
        setUploadedFile(file);
        await processAudioBlob(file);
      } catch (error) {
        // Log decode/convert failures for uploaded audio files
        console.error("Error processing uploaded file:", error);
      }
    },
    [processAudioBlob]
  );

  const formatTime = useCallback((seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? `0${s}` : s}`;
  }, []);

  useEffect(() => {
    let id;
    if (isRecording) {
      id = setInterval(() => {
        setTime((prev) => {
          if (prev < maxTime) return prev + 1;
          stopRecording();
          return prev;
        });
      }, 1000);
    }
    return () => clearInterval(id);
  }, [isRecording, maxTime, stopRecording]);

  const storeRecording = useCallback(async () => {
    const check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      { userId: JSON.parse(localStorage.getItem("user")).userId.S }
    );
    if (check.status !== 200) return;
    const user = check.data.user;
    const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
    if (!user?.relatedOrgs?.L?.find((e) => e?.M?.org_id?.S === orgId)) {
      window.location.reload();
      return;
    }
    try {
      setLoader(true);
      const formData = new FormData();
      if (audioBlob) {
        formData.append(
          "file",
          audioBlob,
          `${recording_data.title}-${Date.now()}.${audioBlob.type.split("/")[1]}`
        );
      } else {
        formData.append(
          "file",
          uploadedFile,
          `${recording_data.title}-${Date.now()}.${uploadedFile.name.split(".").pop().toLowerCase()}`
        );
      }
      const record = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const recording = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "add-recording",
        {
          title: recording_data.title,
          type: record_type,
          url: record.data.url,
          organization_id: orgId,
        }
      );
      localStorage.setItem("organization", JSON.stringify(recording.data.updatedItem));
      const updatedRecordings = recording.data.updatedItem.recordings.L;
      restartRecording();
      setRecordingData({ title: "" });
      setLoader(false);
      posthog.capture("Added a recording", {
        email: JSON.parse(localStorage.getItem("user")).email.S,
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      });
      setStep(1);
      onSuccessRef.current?.(updatedRecordings);
    } catch (error) {
      setLoader(false);
      if (error.response?.data?.message) {
        setSnackMsg(error.response.data.message);
        setOpenSnack(true);
      }
    }
  }, [recording_data, record_type, audioBlob, uploadedFile, restartRecording, setStep]);

  return {
    record_type,
    setRecordType,
    uploadedFile,
    audioPreviewUrl,
    setUploadedFile,
    buttonState,
    time,
    maxTime,
    step,
    setStep,
    recording_data,
    setRecordingData,
    isConverting,
    isRecording,
    loader,
    openSnack,
    setOpenSnack,
    snackMsg,
    setSnackMsg,
    fileInputRef,
    handleFileUpload,
    handleClick,
    formatTime,
    restartRecording,
    storeRecording,
  };
}
